import React from "react";
import { Box, Flex, MenuItem, Select, SelectChangeEvent, TextField, Typography } from "flux";
import { LoraTrainingData } from "../../utils/model-training";
import useThemes from "../../hooks/use-themes";
import useExperiments from "../../hooks/use-experiments";

interface LoraTrainingFormProps {
  data: LoraTrainingData;
  onChange: (data: LoraTrainingData) => void;
  brandId?: number;
}

interface ReplicateModel {
  id: string;
  name: string;
}

const LoraTrainingForm: React.FC<LoraTrainingFormProps> = ({ data, onChange, brandId }) => {
  const { themes } = useThemes(brandId);
  const { experiments } = useExperiments(brandId);

  const handleThemeChange = (event: SelectChangeEvent<unknown>) => {
    updateField("themeId", event.target.value as number);
  };

  const updateField = <K extends keyof LoraTrainingData>(field: K, value: LoraTrainingData[K]) => {
    const newData = {
      ...data,
      [field]: value
    };
    onChange(newData);
  };

  const trainerNames: ReplicateModel[] = [
    {
      name: "Default",
      id: "default"
    },
    {
      name: "Fast Flux Trainer",
      id: "replicate/fast-flux-trainer:8b10794665aed907bb98a1a5324cd1d3a8bea0e9b31e65210967fb9c9e2e08ed"
    }
  ];

  return (
    <Flex flexDirection="column">
      <Box pb="8px">
        <Typography variant="body1">Trainer Selection (Optional)</Typography>
      </Box>
      <Select value={data.trainerName} onChange={(e) => updateField("trainerName", String(e.target.value))}>
        {trainerNames.map((model) => (
          <MenuItem key={model.id} value={model.id}>
            {model.name}
          </MenuItem>
        ))}
      </Select>
      <Box pb="8px">
        <Typography variant="body1">Steps</Typography>
      </Box>
      <TextField
        type="number"
        name="steps"
        value={data.steps}
        onChange={(e) => updateField("steps", Number(e.target.value))}
        placeholder="Steps"
        required
        fullWidth
        dataDogIdentifier="lora training steps"
      />
      <Box pb="8px">
        <Typography variant="body1">Lora rank</Typography>
      </Box>
      <TextField
        type="number"
        name="loraRank"
        value={data.loraRank}
        onChange={(e) => updateField("loraRank", Number(e.target.value))}
        placeholder="Lora Rank"
        required
        fullWidth
        dataDogIdentifier="lora training rank"
      />
      <Box pb="8px">
        <Typography variant="body1">Batch size</Typography>
      </Box>
      <TextField
        type="number"
        name="batchSize"
        value={data.batchSize}
        onChange={(e) => updateField("batchSize", Number(e.target.value))}
        placeholder="Batch Size"
        required
        fullWidth
        dataDogIdentifier="lora training batch size"
      />
      <Box pb="8px">
        <Typography variant="body1">Learning rate</Typography>
      </Box>
      <TextField
        type="number"
        name="learningRate"
        value={data.learningRate}
        onChange={(e) => updateField("learningRate", Number(e.target.value))}
        placeholder="Learning Rate"
        required
        fullWidth
        dataDogIdentifier="lora training learning rate"
      />
      <Box pb="8px">
        <Typography variant="body1">Optimizer</Typography>
      </Box>
      <TextField
        type="text"
        name="optimizer"
        value={data.optimizer}
        onChange={(e) => updateField("optimizer", e.target.value)}
        placeholder="Optimizer"
        required
        fullWidth
        dataDogIdentifier="lora training optimizer"
      />
      <Box pb="8px">
        <Typography variant="body1">Resolution</Typography>
      </Box>
      <TextField
        type="text"
        name="resolution"
        value={data.resolution}
        onChange={(e) => updateField("resolution", e.target.value)}
        placeholder="Resolution"
        required
        fullWidth
        dataDogIdentifier="lora training resolution"
      />
      <Box pb="8px">
        <Typography variant="body1">Caption dropout rate</Typography>
      </Box>
      <TextField
        type="number"
        name="captionDropoutRate"
        value={data.captionDropoutRate}
        multiline={true}
        onChange={(e) => updateField("captionDropoutRate", Number(e.target.value))}
        placeholder="Caption Dropout Rate"
        size="medium"
        required
        fullWidth
        dataDogIdentifier="lora training caption dropout rate"
      />
      <Typography variant="body1">Campaign</Typography>
      <Select value={data.themeId.toString()} onChange={handleThemeChange} fullWidth={true} required>
        {themes?.map((t) => (
          <MenuItem key={t.id} value={t.id} size="medium">
            {t.name || `Theme ${t.id}`}
          </MenuItem>
        ))}
      </Select>
      <Typography variant="body1">Base LoRA (Optional)</Typography>
      <Select
        value={data.baseLoraId || ""}
        onChange={(e) => updateField("baseLoraId", Number(e.target.value))}
        fullWidth={true}
      >
        <MenuItem value="">
          <em>None</em>
        </MenuItem>
        {experiments?.map((ex) => (
          <MenuItem key={ex.id} value={ex.id} title={ex.description} size="medium">
            {ex.name || `Experiment ${ex.id}`}
          </MenuItem>
        ))}
      </Select>
      <Typography variant="body1">Content/Style/Balanced Training. If you don&apos;t know, select Balanced</Typography>
      <Select
        value={data.contentOrStyle}
        onChange={(e) => updateField("contentOrStyle", String(e.target.value))}
        fullWidth={true}
      >
        <MenuItem value="balanced" size="medium">
          Balanced
        </MenuItem>
        <MenuItem value="content" size="medium">
          Content
        </MenuItem>
        <MenuItem value="style" size="medium">
          Style
        </MenuItem>
        <MenuItem value="subject" size="medium">
          Subject (Only for Fast Flux Trainer)
        </MenuItem>
      </Select>
      <Box pb="8px">
        <Typography variant="body1">Wandb Sample Interval (0 means we don&apos;t sample)</Typography>
      </Box>
      <TextField
        type="number"
        name="wandbSampleInterval"
        value={data.wandbSampleInterval}
        onChange={(e) => updateField("wandbSampleInterval", Number(e.target.value))}
        placeholder="Wandb Sample Interval"
        fullWidth
        dataDogIdentifier="lora training wandb sample prompt"
      />
      <Box pb="8px">
        <Typography variant="body1">Wandb Sample Prompt</Typography>
      </Box>
      <TextField
        type="text"
        name="prompt"
        value={data.wandbSamplePrompt}
        multiline={true}
        onChange={(e) => updateField("wandbSamplePrompt", String(e.target.value))}
        placeholder="Wandb Sample Prompt"
        size="medium"
        fullWidth
        dataDogIdentifier="lora training wandb sample prompt"
      />
      <Box pb="8px">
        <Typography variant="body1">Base Model Name or URL (Optional)</Typography>
      </Box>
      <TextField
        type="text"
        name="prompt"
        value={data.baseModelNameOrPath}
        multiline={true}
        onChange={(e) => updateField("baseModelNameOrPath", String(e.target.value))}
        placeholder="Base Model Name or URL"
        size="medium"
        fullWidth
        dataDogIdentifier="lora training base model name or url"
      />
    </Flex>
  );
};

export default LoraTrainingForm;
